import { lazy, Suspense } from 'react'
import {
    LAYOUT_COLLAPSIBLE_SIDE,
    LAYOUT_BLANK,
} from '@/constants/theme.constant'
import Loading from '@/components/shared/Loading'
import LocaleSync from '@/components/template/LocaleSync'
import TimezoneSync from '@/components/template/TimezoneSync'
import type { CommonProps } from '@/@types/common'
import type { LazyExoticComponent, JSX } from 'react'
import type { LayoutType } from '@/@types/theme'

type Layouts = Record<
    string,
    LazyExoticComponent<<T extends CommonProps>(props: T) => JSX.Element>
>

interface PostLoginLayoutProps extends CommonProps {
    layoutType: LayoutType
}

/**
 * The template ships six post-login layouts and a UI to switch between them.
 * We kept two: the collapsible side nav every dashboard page uses, and blank
 * for full-bleed pages. The registry stays a map rather than collapsing to a
 * single import so adding one back is a one-line change.
 */
const layouts: Layouts = {
    [LAYOUT_COLLAPSIBLE_SIDE]: lazy(
        () => import('./components/CollapsibleSide'),
    ),
    [LAYOUT_BLANK]: lazy(() => import('./components/Blank')),
}

const PostLoginLayout = ({ layoutType, children }: PostLoginLayoutProps) => {
    const AppLayout = layouts[layoutType] ?? layouts[Object.keys(layouts)[0]]

    return (
        <Suspense
            fallback={
                <div className="flex flex-auto flex-col h-[100vh]">
                    <Loading loading={true} />
                </div>
            }
        >
            {/* Renders nothing; here rather than in one of the layouts so it
                runs whichever the user lands on, and outside the layout so a
                layout switch does not re-run the reconciliation. */}
            <LocaleSync />
            <TimezoneSync />
            <AppLayout>{children}</AppLayout>
        </Suspense>
    )
}

export default PostLoginLayout
